<template>
  <div class="album-stat-v3">
    <div class="stat-section-header">
      <div class="header-title-group">
        <div class="header-icon-badge"><i class="el-icon-picture-outline"></i></div>
        <div>
          <h2 class="header-title">Thống Kê Album Hoạt Động &amp; Ảnh Học Sinh</h2>
          <p class="header-subtitle">Album tạo trong kỳ; trạng thái duyệt tại thời điểm xem</p>
        </div>
      </div>
      <div v-if="overview" class="header-badge-group">
        <span class="live-indicator"><span class="pulse-dot"></span>
          Tổng album: {{ overview.totalAlbums }} • Đã duyệt: {{ overview.approvedAlbums }} ({{ overview.approvalRate }}%)
        </span>
      </div>
    </div>

    <div class="filter-control-bar">
      <div class="filter-left">
        <div class="filter-mode-pills">
          <button :class="['mode-pill-btn', { active: filterMode === 'DAY' }]" @click="changeFilterMode('DAY')"><i class="el-icon-date"></i> Theo ngày</button>
          <button :class="['mode-pill-btn', { active: filterMode === 'WEEK' }]" @click="changeFilterMode('WEEK')"><i class="el-icon-time"></i> Theo tuần</button>
          <button :class="['mode-pill-btn', { active: filterMode === 'RANGE' }]" @click="changeFilterMode('RANGE')"><i class="el-icon-calendar"></i> Khoảng thời gian</button>
        </div>
        <div v-if="filterMode === 'DAY'" class="filter-chip">
          <el-date-picker v-model="selectedDate" type="date" size="small" format="dd/MM/yyyy"
            value-format="yyyy-MM-dd" :clearable="false" :picker-options="pickerOptions"
            class="filter-date-input" @change="refreshData" />
        </div>
        <div v-else-if="filterMode === 'WEEK'" class="filter-week-group">
          <el-date-picker v-model="selectedWeekDate" type="week" size="small" format="Tuần WW (yyyy)"
            :clearable="false" :picker-options="pickerOptions" class="filter-week-input" @change="refreshData" />
          <div class="week-nav-btn-group">
            <button class="nav-arrow-btn" title="Tuần trước" @click="moveWeek(-1)"><i class="el-icon-arrow-left"></i></button>
            <button class="nav-current-btn" @click="currentWeek">Tuần này</button>
            <button class="nav-arrow-btn" title="Tuần sau" :disabled="!canMoveNextWeek" @click="moveWeek(1)"><i class="el-icon-arrow-right"></i></button>
          </div>
          <span class="range-display-tag">{{ weekRangeLabel }}</span>
        </div>
        <div v-else class="filter-chip">
          <el-date-picker v-model="selectedDateRange" type="daterange" size="small"
            range-separator="đến" start-placeholder="Từ ngày" end-placeholder="Đến ngày"
            format="dd/MM/yyyy" value-format="yyyy-MM-dd" :clearable="false"
            :picker-options="pickerOptions" class="filter-range-input" @change="refreshData" />
        </div>
        <div class="filter-chip">
          <span class="chip-label"><i class="el-icon-collection-tag"></i> Khối:</span>
          <el-select v-model="selectedGrade" size="small" class="grade-select" @change="refreshData">
            <el-option label="Tất cả các khối" :value="null" />
            <el-option v-for="grade in gradeOptions" :key="grade.gradeId"
              :label="grade.gradeName" :value="grade.gradeId" />
          </el-select>
        </div>
      </div>
      <div class="filter-right">
        <el-button type="primary" size="small" icon="el-icon-refresh-right" class="btn-refresh"
          :loading="loadingOverview" @click="refreshData">Làm mới</el-button>
      </div>
    </div>

    <el-alert v-if="apiError" :title="apiError" type="error" :closable="false" show-icon class="stat-api-error" />
    <div v-if="loadingOverview" class="album-loading-state">Đang tải thống kê album...</div>
    <div v-else-if="!overview && !apiError" class="album-loading-state">Chưa có dữ liệu thống kê album.</div>

    <div class="album-kpi-grid" :class="{ 'album-stat-disabled': !overview }">
      <div class="album-kpi-card border-purple interactive-card" @click="openDialog('ALL')">
        <div class="kpi-icon-wrap bg-purple-soft text-purple"><i class="el-icon-picture"></i></div>
        <div class="kpi-details"><div class="kpi-title-row"><span class="kpi-name">Tổng album của trường</span><span class="click-hint-badge text-purple">Chi tiết <i class="el-icon-arrow-right"></i></span></div>
          <div class="kpi-number text-purple">{{ metric('totalAlbums') }} <span class="kpi-unit">album</span></div>
          <div class="kpi-hint">Đã đăng từ {{ metric('publishingClasses') }} lớp học</div>
        </div>
      </div>
      <div class="album-kpi-card border-emerald interactive-card" @click="openDialog('APPROVED')">
        <div class="kpi-icon-wrap bg-emerald-soft text-emerald"><i class="el-icon-circle-check"></i></div>
        <div class="kpi-details"><div class="kpi-title-row"><span class="kpi-name">Album đã được duyệt</span><span class="click-hint-badge text-emerald">Chi tiết <i class="el-icon-arrow-right"></i></span></div>
          <div class="kpi-number text-emerald">{{ metric('approvedAlbums') }} <span class="kpi-unit">album</span></div>
          <div class="kpi-hint text-emerald">Đạt {{ metric('approvalRate') }}% tổng số album</div>
        </div>
      </div>
      <div class="album-kpi-card border-amber interactive-card" @click="openDialog('PENDING')">
        <div class="kpi-icon-wrap bg-amber-soft text-amber"><i class="el-icon-warning-outline"></i></div>
        <div class="kpi-details"><div class="kpi-title-row"><span class="kpi-name">Album chờ duyệt</span><span class="click-hint-badge text-amber">Cần duyệt <i class="el-icon-arrow-right"></i></span></div>
          <div class="kpi-number text-amber">{{ metric('pendingAlbums') }} <span class="kpi-unit">album</span></div>
          <div class="kpi-hint text-amber">Cần duyệt để phụ huynh xem</div>
        </div>
      </div>
    </div>

    <el-row :gutter="20" class="charts-row">
      <el-col :xs="24" :sm="24" :md="15" :lg="16">
        <div class="chart-card h-100">
          <div class="chart-card-head"><div><h3 class="chart-title">Số Lượng Album Của Từng Lớp Đã Đăng &amp; Đã Duyệt</h3>
            <p class="chart-subtitle">Một album gắn nhiều lớp được tính ở từng lớp liên quan</p></div>
            <div class="chart-legend-chips"><span class="legend-dot-item"><span class="chip-color chip-approved"></span> Đã được duyệt</span>
              <span class="legend-dot-item"><span class="chip-color chip-pending"></span> Chờ duyệt</span></div>
          </div>
          <div ref="chartClassAlbums" class="chart-box-main"></div>
        </div>
      </el-col>
      <el-col :xs="24" :sm="24" :md="9" :lg="8">
        <div class="chart-card h-100 doughnut-card">
          <div class="chart-card-head"><div><h3 class="chart-title">Phân Bổ Album Theo Khối</h3>
            <p class="chart-subtitle">Mỗi album nằm ở một nhóm phân bổ</p></div>
            <span class="badge-total-pill">{{ metric('totalAlbums') }} Album</span></div>
          <div ref="chartGradeAlbums" class="chart-box-doughnut"></div>
          <div class="grade-legend-list">
            <div v-for="item in displayedDistribution" :key="item.key" class="grade-legend-row">
              <div class="legend-left"><span class="color-dot" :style="{ backgroundColor: colorFor(item) }"></span><span class="legend-name">{{ item.name }}</span></div>
              <div class="legend-right"><strong class="legend-count">{{ item.albums }} album</strong><span class="legend-pct">({{ item.percentage }}%)</span></div>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>

    <el-dialog :visible.sync="dialogVisible" width="68%" top="6vh" custom-class="modern-album-dialog" :destroy-on-close="true" @close="closeDialog">
      <div slot="title" class="dialog-custom-header"><div class="dialog-icon-badge"><i class="el-icon-picture"></i></div>
        <div><h3 class="dialog-heading">{{ dialogTitle }}</h3><p class="dialog-subheading">{{ activeFilterDesc }}</p></div></div>
      <div class="dialog-filter-bar">
        <div class="dialog-tab-group">
          <button :class="['d-tab-btn', { active: dialogTab === 'ALL' }]" @click="changeDialogTab('ALL')">Tất cả</button>
          <button :class="['d-tab-btn', 'tab-success', { active: dialogTab === 'APPROVED' }]" @click="changeDialogTab('APPROVED')">Đã duyệt</button>
          <button :class="['d-tab-btn', 'tab-warning', { active: dialogTab === 'PENDING' }]" @click="changeDialogTab('PENDING')">Chờ duyệt</button>
        </div>
        <div class="dialog-search"><el-input v-model="dialogSearch" size="small" placeholder="Tìm tên album, lớp, người đăng..."
          prefix-icon="el-icon-search" clearable @input="scheduleDetailSearch" /></div>
      </div>
      <el-alert v-if="dialogError" :title="dialogError" type="error" :closable="false" show-icon />
      <div v-loading="loadingDialog" class="dialog-album-grid-container">
        <div v-if="!loadingDialog && detailItems.length === 0" class="empty-album-state"><i class="el-icon-picture-outline"></i><p>Không có album trong danh sách này</p></div>
        <div v-for="album in detailItems" :key="album.idx" class="album-card-item">
          <div class="album-thumb-container">
            <img v-if="album.displayCoverUrl" :src="album.displayCoverUrl" :alt="album.name"
              class="album-stat-cover" @error="$set(album, 'displayCoverUrl', null)" />
            <div v-else class="album-cover-placeholder"><i class="el-icon-camera cover-icon"></i></div>
            <span class="photo-pill-badge"><i class="el-icon-picture"></i> {{ album.photoCount }} ảnh</span>
            <span class="status-pill-badge" :class="album.approved ? 'badge-approved' : 'badge-pending'">{{ album.approved ? 'Đã duyệt' : 'Chờ duyệt' }}</span>
          </div>
          <div class="album-card-body"><h4 class="album-item-title" :title="album.name">{{ album.name }}</h4>
            <div class="album-item-meta"><span class="album-item-class"><i class="el-icon-s-home"></i> {{ album.classNames.join(', ') || 'Chưa phân bổ' }}</span>
              <span class="album-item-date"><i class="el-icon-date"></i> {{ formatDate(album.createdDate) }}</span></div>
            <div class="album-item-teacher"><i class="el-icon-user"></i> Người đăng: <b>{{ album.createdBy || 'Không rõ' }}</b></div>
            <div class="album-item-foot"><span></span><el-button v-if="!album.approved" type="success" size="mini" icon="el-icon-check"
              :loading="approvingIdx === album.idx" @click="approveAlbum(album)">Duyệt ngay</el-button>
              <router-link v-else :to="`/album-v2/detail/${encodeURIComponent(album.idx)}`" class="view-photos-link">Xem {{ album.photoCount }} ảnh</router-link>
            </div>
          </div>
        </div>
      </div>
      <div v-if="detailTotal > detailSize" class="album-stat-pagination"><el-pagination layout="prev, pager, next" :total="detailTotal"
        :page-size="detailSize" :current-page="detailPage + 1" @current-change="changeDetailPage" /></div>
      <div slot="footer" class="dialog-footer"><el-button size="small" @click="dialogVisible = false">Đóng</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from "echarts";
import moment from "moment";
import AlbumStatisticsService from "@/services/dashboard_v3/AlbumStatisticsService";
import AlbumImageUrlService from "@/modules/album/services/AlbumImageUrlService";

export default {
  name: "AlbumStatisticsV3",
  props: {
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hasLoadedData: false,
      isLoadingData: false,
      filterMode: "DAY",
      selectedDate: moment().format("YYYY-MM-DD"),
      selectedWeekDate: moment().toDate(),
      selectedDateRange: [moment().startOf("isoWeek").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      selectedGrade: null,
      overview: null,
      gradeOptions: [],
      apiError: "",
      loadingOverview: false,
      overviewRequestId: 0,
      dialogVisible: false,
      dialogTab: "ALL",
      dialogClassId: null,
      dialogClassName: "",
      dialogSearch: "",
      dialogError: "",
      detailItems: [],
      detailTotal: 0,
      detailPage: 0,
      detailSize: 20,
      detailRequestId: 0,
      loadingDialog: false,
      approvingIdx: null,
      searchTimer: null,
      chartClass: null,
      chartGrade: null,
      pickerOptions: { disabledDate: date => date.getTime() > Date.now() }
    };
  },
  computed: {
    weekRangeLabel() {
      const week = moment(this.selectedWeekDate);
      return `Tuần ${week.isoWeek()} (${week.clone().startOf("isoWeek").format("DD/MM/YYYY")} - ${week.clone().endOf("isoWeek").format("DD/MM/YYYY")})`;
    },
    canMoveNextWeek() {
      return moment(this.selectedWeekDate).startOf("isoWeek").isBefore(moment().startOf("isoWeek"));
    },
    activeFilterDesc() {
      if (this.filterMode === "DAY") return `Ngày ${moment(this.selectedDate).format("DD/MM/YYYY")}`;
      if (this.filterMode === "WEEK") return this.weekRangeLabel;
      return `${moment(this.selectedDateRange[0]).format("DD/MM/YYYY")} - ${moment(this.selectedDateRange[1]).format("DD/MM/YYYY")}`;
    },
    displayedDistribution() {
      return this.overview ? this.overview.gradeDistribution.filter(item => item.albums > 0) : [];
    },
    dialogTitle() {
      if (this.dialogClassId) return `Danh sách album của lớp: ${this.dialogClassName}`;
      if (this.dialogTab === "APPROVED") return "Danh sách album đã duyệt";
      if (this.dialogTab === "PENDING") return "Danh sách album chờ duyệt";
      return "Tất cả album ảnh hoạt động của học sinh";
    }
  },
  mounted() {
    this.chartClass = echarts.init(this.$refs.chartClassAlbums);
    this.chartGrade = echarts.init(this.$refs.chartGradeAlbums);
    this.chartClass.on("click", params => {
      const classes = this.overview ? this.overview.classes : [];
      const cls = classes[params.dataIndex];
      if (cls) this.openDialog("ALL", cls);
    });
    window.addEventListener("resize", this.handleResize);
    if (this.autoLoad) {
      this.loadData();
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.overviewRequestId++;
    this.detailRequestId++;
    if (this.chartClass) this.chartClass.dispose();
    if (this.chartGrade) this.chartGrade.dispose();
  },
  methods: {
    async loadData() {
      if (this.hasLoadedData || this.isLoadingData) return;
      this.isLoadingData = true;
      try {
        await this.refreshData();
        this.hasLoadedData = true;
      } finally {
        this.isLoadingData = false;
      }
    },
    metric(key) { return this.overview ? this.overview[key] : "—"; },
    colorFor(item) {
      if (item.key === "MULTI_GRADE") return "#8b5cf6";
      if (item.key === "SCHOOL") return "#64748b";
      if (item.key === "UNALLOCATED") return "#94a3b8";
      const index = this.gradeOptions.findIndex(grade => `GRADE_${grade.gradeId}` === item.key);
      return ["#ec4899", "#f59e0b", "#10b981", "#6366f1", "#06b6d4"][Math.max(0, index) % 5];
    },
    requestParams() {
      const params = { mode: this.filterMode };
      if (this.filterMode === "DAY") params.date = this.selectedDate;
      if (this.filterMode === "WEEK") params.date = moment(this.selectedWeekDate).format("YYYY-MM-DD");
      if (this.filterMode === "RANGE") {
        params.startDate = this.selectedDateRange[0];
        params.endDate = this.selectedDateRange[1];
      }
      if (this.selectedGrade != null) params.gradeId = this.selectedGrade;
      return params;
    },
    changeFilterMode(mode) { this.filterMode = mode; this.refreshData(); },
    moveWeek(delta) {
      if (delta > 0 && !this.canMoveNextWeek) return;
      this.selectedWeekDate = moment(this.selectedWeekDate).add(delta, "weeks").toDate();
      this.refreshData();
    },
    currentWeek() { this.selectedWeekDate = moment().toDate(); this.refreshData(); },
    async refreshData() {
      const requestId = ++this.overviewRequestId;
      this.loadingOverview = true;
      this.apiError = "";
      this.overview = null;
      this.renderCharts();
      if (this.dialogVisible) this.dialogVisible = false;
      try {
        const response = await AlbumStatisticsService.getOverview(this.requestParams());
        if (requestId !== this.overviewRequestId) return;
        const data = response && response.data && response.data.data;
        if (!data || !Array.isArray(data.classes) || !Array.isArray(data.gradeDistribution)) throw new Error("API trả dữ liệu album không hợp lệ");
        this.overview = data;
        this.gradeOptions = data.gradeOptions || [];
        this.$nextTick(this.renderCharts);
      } catch (error) {
        if (requestId === this.overviewRequestId) this.apiError = this.errorMessage(error, "Không tải được thống kê album");
      } finally {
        if (requestId === this.overviewRequestId) this.loadingOverview = false;
      }
    },
    renderCharts() {
      if (!this.chartClass || !this.chartGrade) return;
      const classes = this.overview ? this.overview.classes : [];
      this.chartClass.setOption({
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, formatter: params => {
          const cls = classes[params[0].dataIndex];
          if (!cls) return "";
          return `${cls.className}<br/>Tổng: ${cls.totalAlbums} album • ${cls.photoCount} ảnh<br/>Đã duyệt: ${cls.approvedAlbums}<br/>Chờ duyệt: ${cls.pendingAlbums}`;
        } },
        grid: { left: "3%", right: "4%", top: "14%", bottom: "16%", containLabel: true },
        xAxis: { type: "category", data: classes.map(item => item.className), axisLabel: { interval: 0, rotate: 28, fontSize: 11 } },
        yAxis: { type: "value", minInterval: 1 },
        dataZoom: [{ type: "slider", show: classes.length > 7, start: 0, end: classes.length > 7 ? 700 / classes.length : 100, height: 14, bottom: 2 }, { type: "inside" }],
        series: [
          { name: "Đã được duyệt", type: "bar", stack: "total", barWidth: 26, itemStyle: { color: "#34b581" }, label: { show: true, position: "inside", formatter: p => p.value || "" }, data: classes.map(item => item.approvedAlbums) },
          { name: "Chờ duyệt", type: "bar", stack: "total", barWidth: 26, itemStyle: { color: "#f59e0b" }, label: { show: true, position: "inside", formatter: p => p.value || "" }, data: classes.map(item => item.pendingAlbums) }
        ]
      }, true);
      const distribution = this.displayedDistribution;
      this.chartGrade.setOption({
        tooltip: { trigger: "item", formatter: "{b}: {c} album ({d}%)" },
        series: [{ type: "pie", radius: ["52%", "78%"], center: ["50%", "50%"], label: { show: false },
          itemStyle: { borderRadius: 6, borderColor: "#ffffff", borderWidth: 2 },
          data: distribution.map(item => ({ name: item.name, value: item.albums, itemStyle: { color: this.colorFor(item) } })) }]
      }, true);
    },
    handleResize() { if (this.chartClass) this.chartClass.resize(); if (this.chartGrade) this.chartGrade.resize(); },
    openDialog(tab, cls = null) {
      if (!this.overview) return;
      this.dialogClassId = cls ? cls.classId : null;
      this.dialogClassName = cls ? cls.className : "";
      this.dialogTab = tab;
      this.dialogSearch = "";
      this.detailPage = 0;
      this.dialogVisible = true;
      this.fetchDetails();
    },
    closeDialog() {
      this.detailRequestId++;
      this.detailItems = [];
      if (this.searchTimer) clearTimeout(this.searchTimer);
    },
    changeDialogTab(tab) { this.dialogTab = tab; this.detailPage = 0; this.fetchDetails(); },
    scheduleDetailSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.detailRequestId++;
      this.detailItems = [];
      this.loadingDialog = true;
      this.searchTimer = setTimeout(() => { this.detailPage = 0; this.fetchDetails(); }, 300);
    },
    changeDetailPage(page) { this.detailPage = page - 1; this.fetchDetails(); },
    async fetchDetails() {
      const requestId = ++this.detailRequestId;
      this.loadingDialog = true;
      this.dialogError = "";
      this.detailItems = [];
      try {
        const params = { ...this.requestParams(), approval: this.dialogTab, page: this.detailPage, size: this.detailSize };
        if (this.dialogClassId != null) params.classId = this.dialogClassId;
        if (this.dialogSearch.trim()) params.search = this.dialogSearch.trim();
        const response = await AlbumStatisticsService.getItems(params);
        if (requestId !== this.detailRequestId) return;
        const data = response && response.data && response.data.data;
        if (!data || !Array.isArray(data.items)) throw new Error("API trả danh sách album không hợp lệ");
        this.detailItems = data.items;
        this.detailTotal = data.total;
        this.resolveCovers(data.items, requestId);
      } catch (error) {
        if (requestId === this.detailRequestId) this.dialogError = this.errorMessage(error, "Không tải được danh sách album");
      } finally {
        if (requestId === this.detailRequestId) this.loadingDialog = false;
      }
    },
    resolveCovers(items, requestId) {
      items.forEach(async album => {
        const source = album.representativePictureUrl;
        if (this.isHttpImage(source)) {
          this.$set(album, "displayCoverUrl", source);
        } else if (source && (album.representativeStorageStatus === "MI_SV" || album.representativeStorageStatus === "MI_0")) {
          try {
            const url = await AlbumImageUrlService.resolve({ urlMinio: source, storageStatus: "MI_SV" });
            if (requestId === this.detailRequestId) this.$set(album, "displayCoverUrl", url);
          } catch (error) {
            if (requestId === this.detailRequestId) this.$set(album, "displayCoverUrl", null);
          }
        } else if (source) {
          this.$set(album, "displayCoverUrl", source);
        }
      });
    },
    async approveAlbum(album) {
      if (this.approvingIdx) return;
      this.approvingIdx = album.idx;
      try {
        await AlbumStatisticsService.approve(album.idx);
        this.$message.success("Đã duyệt album");
        await this.refreshOverviewAfterApproval();
        if (this.dialogVisible) await this.fetchDetails();
      } catch (error) {
        this.dialogError = this.errorMessage(error, "Không duyệt được album");
      } finally {
        this.approvingIdx = null;
      }
    },
    async refreshOverviewAfterApproval() {
      const requestId = ++this.overviewRequestId;
      try {
        const response = await AlbumStatisticsService.getOverview(this.requestParams());
        if (requestId !== this.overviewRequestId) return;
        const data = response && response.data && response.data.data;
        if (!data || !Array.isArray(data.classes)) throw new Error("API trả dữ liệu album không hợp lệ");
        this.overview = data;
        this.$nextTick(this.renderCharts);
      } catch (error) {
        if (requestId === this.overviewRequestId) this.apiError = this.errorMessage(error, "Không tải lại được thống kê album");
      }
    },
    formatDate(value) { return value ? moment(value).format("DD/MM/YYYY") : "—"; },
    isHttpImage(url) { return /^https?:\/\//i.test(url || ""); },
    errorMessage(error, fallback) {
      return (error && error.response && error.response.data && error.response.data.message) ||
        (error && error.message) || fallback;
    }
  }
};
</script>
<style scoped>
.album-stat-v3 {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px -3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
}

/* Header */
.stat-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #f3e8ff;
  color: #8b5cf6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.header-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.header-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #7c3aed;
  background: #f5f3ff;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #ddd6fe;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.25);
  animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
}

/* 1. Control Toolbar */
.filter-control-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 16px;
  margin-bottom: 22px;
}

.filter-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-mode-pills {
  display: inline-flex;
  background: #e2e8f0;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.mode-pill-btn {
  border: none;
  background: transparent;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.15s ease;
}

.mode-pill-btn.active {
  background: #ffffff;
  color: #7c3aed;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.filter-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chip-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.filter-date-input {
  width: 145px;
}

.filter-week-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-week-input {
  width: 165px;
}

.filter-range-input {
  width: 250px;
}

.grade-select {
  width: 185px;
}

.week-nav-btn-group {
  display: inline-flex;
  background: #e2e8f0;
  border-radius: 8px;
  padding: 2px;
}

.nav-arrow-btn {
  border: none;
  background: transparent;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  transition: all 0.15s ease;
}

.nav-arrow-btn:hover {
  background: #ffffff;
  color: #7c3aed;
}

.nav-current-btn {
  border: none;
  background: transparent;
  padding: 0 10px;
  height: 28px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.nav-current-btn:hover {
  background: #ffffff;
  color: #7c3aed;
}

.range-display-tag {
  font-size: 11.5px;
  font-weight: 700;
  color: #6366f1;
  background: #e0e7ff;
  padding: 4px 10px;
  border-radius: 6px;
}

.filter-right {
  display: flex;
  align-items: center;
}

.btn-refresh {
  border-radius: 8px;
}

/* 2. Cụm 3 Thẻ Chỉ Số KPI */
.album-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.album-kpi-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: all 0.2s ease;
}

.interactive-card {
  cursor: pointer;
  position: relative;
}

.interactive-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
}

.border-purple { border-left: 5px solid #8b5cf6; }
.border-emerald { border-left: 5px solid #10b981; }
.border-amber { border-left: 5px solid #f59e0b; }
.border-blue { border-left: 5px solid #0284c7; }
.border-pink { border-left: 5px solid #ec4899; }

.kpi-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.bg-purple-soft { background: #f3e8ff; }
.text-purple { color: #8b5cf6; }
.bg-emerald-soft { background: #dcfce7; }
.text-emerald { color: #10b981; }
.bg-amber-soft { background: #fef3c7; }
.text-amber { color: #d97706; }
.bg-blue-soft { background: #e0f2fe; }
.text-blue { color: #0284c7; }
.bg-pink-soft { background: #fce7f3; }
.text-pink { color: #ec4899; }
.bg-gray-soft { background: #f1f5f9; }
.text-muted { color: #94a3b8; }

.kpi-details {
  flex: 1;
  min-width: 0;
}

.kpi-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.kpi-name {
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
  display: block;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.click-hint-badge {
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #ffffff;
  padding: 2px 6px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.kpi-number {
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 4px;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
}

.kpi-hint {
  font-size: 11.5px;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 3. Charts Area */
.charts-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.charts-row::before,
.charts-row::after {
  display: none;
}

.charts-row > .el-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.chart-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}

.chart-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.chart-title {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.chart-subtitle {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.chart-legend-chips {
  display: flex;
  gap: 12px;
  align-items: center;
}

.legend-dot-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.chip-color {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.chip-approved { background: #10b981; }
.chip-pending { background: #f59e0b; }

.chart-box-main {
  height: 310px;
  width: 100%;
}

.doughnut-card {
  display: flex;
  flex-direction: column;
}

.badge-total-pill {
  font-size: 11.5px;
  font-weight: 700;
  color: #7c3aed;
  background: #f3e8ff;
  padding: 3px 10px;
  border-radius: 999px;
}

.chart-box-doughnut {
  height: 180px;
  width: 100%;
}

.grade-legend-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}

.grade-legend-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.legend-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.legend-name {
  color: #334155;
  font-weight: 600;
}

.legend-right {
  display: flex;
  gap: 6px;
  align-items: center;
}

.legend-count {
  color: #0f172a;
  font-weight: 700;
}

.legend-pct {
  color: #64748b;
  font-size: 11px;
}


/* 5. Dialog Styles */
.dialog-custom-header {
  display: flex;
  align-items: center;
  gap: 14px;
}

.dialog-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.badge-purple { background: #f3e8ff; color: #8b5cf6; }
.badge-emerald { background: #dcfce7; color: #10b981; }
.badge-amber { background: #fef3c7; color: #d97706; }

.dialog-heading {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subheading {
  margin: 0;
  font-size: 12.5px;
  color: #64748b;
}

.dialog-filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 14px;
  flex-wrap: wrap;
  gap: 12px;
}

.dialog-tab-group {
  display: flex;
  gap: 8px;
}

.d-tab-btn {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.d-tab-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.d-tab-btn.active {
  background: #7c3aed;
  color: #ffffff;
  border-color: #7c3aed;
}

.tab-success.active {
  background: #10b981;
  border-color: #10b981;
  color: #ffffff;
}

.tab-warning.active {
  background: #f59e0b;
  border-color: #f59e0b;
  color: #ffffff;
}

.dialog-search {
  width: 250px;
}

.dialog-album-grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  max-height: 480px;
  overflow-y: auto;
  padding-right: 4px;
}

.empty-album-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
}

.empty-album-state i {
  font-size: 40px;
  margin-bottom: 8px;
  display: block;
}

.album-card-item {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
}

.album-card-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.06);
}

.album-thumb-container {
  position: relative;
  height: 120px;
  overflow: hidden;
}

.album-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ffffff;
}

.cover-icon {
  font-size: 32px;
  opacity: 0.85;
}

.cover-topic-tag {
  font-size: 11px;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.2);
  padding: 2px 8px;
  border-radius: 999px;
  margin-top: 4px;
}

.photo-pill-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(15, 23, 42, 0.75);
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
  backdrop-filter: blur(4px);
}

.status-pill-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}

.badge-approved { background: #dcfce7; color: #15803d; }
.badge-pending { background: #fef3c7; color: #b45309; }

.album-card-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.album-item-title {
  margin: 0 0 8px;
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 36px;
}

.album-item-meta {
  display: flex;
  justify-content: space-between;
  font-size: 11.5px;
  color: #64748b;
  margin-bottom: 6px;
}

.album-item-class {
  font-weight: 600;
  color: #6366f1;
}

.album-item-teacher {
  font-size: 11.5px;
  color: #64748b;
  margin-bottom: 10px;
}

.album-item-teacher b {
  color: #334155;
}

.album-item-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}

.social-stats {
  display: flex;
  gap: 10px;
  font-size: 11.5px;
  font-weight: 600;
}

.stat-heart { color: #ec4899; }
.stat-cmt { color: #0284c7; }

.view-photos-link {
  font-size: 11.5px;
  font-weight: 600;
  color: #7c3aed;
  padding: 0;
}



@media (max-width: 768px) {
  .album-kpi-grid {
    grid-template-columns: 1fr;
  }
  .filter-control-bar {
    flex-direction: column;
    align-items: flex-start;
  }
}
.album-loading-state { padding: 18px; text-align: center; color: #64748b; }
.album-stat-disabled { opacity: .55; pointer-events: none; }
.album-stat-pagination { padding: 12px; text-align: center; }
.album-stat-cover { width: 100%; height: 100%; object-fit: cover; }
</style>
